import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption, MatSelect } from '@angular/material/select';
import {
  catchError,
  filter,
  map,
  merge,
  shareReplay,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { InteractionQuestion } from '../../../../../models/interactions.model';
import { BaseDialog } from '../../../../dialog/base-dialog';
import { ConfirmHandler } from '../../../../dialog/dialog.model';

@Component({
  selector: 'app-goto-question-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,

    MatError,
    MatFormField,
    MatSelect,
    MatOption,

    MatProgressSpinner,

    ReactiveFormsModule,
  ],
  templateUrl: './goto-question-dialog.component.html',
  styleUrl: './goto-question-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GotoQuestionDialogComponent extends BaseDialog<{
  questions: InteractionQuestion[];
  onConfirm: ConfirmHandler;
}> {
  private readonly _dialogRef =
    inject<MatDialogRef<GotoQuestionDialogComponent>>(MatDialogRef);

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  protected readonly questions = this.data.questions;

  protected readonly control = new FormControl<InteractionQuestion | null>(null, {
    validators: [Validators.required],
  });

  constructor() {
    super();

    if (!this.questions.length) {
      this.control.disable();
      this.control.markAsTouched();
    }
  }

  override handleConfirm(): void {
    this.submitted.set(true);
    this.control.disable();

    const delay = 1000;

    const callback$ = this.data.onConfirm(this.control.value);

    const source$ = callback$.pipe(
      shareReplay(1),
      catchError((error) => {
        this.submitted.set(false);

        this.control.enable();

        return throwError(() => error);
      })
    );

    const loading$ = merge(
      timer(delay).pipe(
        map(() => true),
        takeUntil(source$)
      ),

      source$.pipe(map(() => false))
    );

    loading$
      .pipe(
        tap((state) => this.loading.set(state)),
        filter((state) => !state),
        tap(() => this._dialogRef.close(true)),
        take(1)
      )
      .subscribe();
  }
}
